import React from 'react';
import { Modal } from 'react-bootstrap';
import { Supplier } from 'types/supplier';
import Button from 'components/Button';
import { Pencil, Trash2, RotateCcw } from 'lucide-react';
import styles from './Suppliers.module.scss';

interface ViewSupplierModalProps {
  show: boolean;
  onHide: () => void;
  supplier: Supplier | null;
  onEdit: () => void;
  onDelete: () => void;
  onRestore: () => void;
  onError: (message: string) => void;
}

function ViewSupplierModal({
  show,
  onHide,
  supplier,
  onEdit,
  onDelete,
  onRestore,
}: ViewSupplierModalProps) {
  if (!supplier) {
    return null;
  }

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      className={styles.modal}
      contentClassName={styles.modalContent}
      dialogClassName={styles.modalDialog}
    >
      <Modal.Header closeButton className={styles.modalHeader}>
        <Modal.Title className={styles.modalTitle}>
          Supplier Details
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className={styles.modalBody}>
        <div className={styles.viewDetails}>
          <div className={styles.detailRow}>
            <strong>Name:</strong>
            <span>{supplier.name}</span>
          </div>
          {supplier.email && (
            <div className={styles.detailRow}>
              <strong>Email:</strong>
              <span>{supplier.email}</span>
            </div>
          )}
          {supplier.phone && (
            <div className={styles.detailRow}>
              <strong>Phone:</strong>
              <span>{supplier.phone}</span>
            </div>
          )}
          {supplier.address && (
            <div className={styles.detailRow}>
              <strong>Address:</strong>
              <span>{supplier.address}</span>
            </div>
          )}
          {supplier.tinNumber && (
            <div className={styles.detailRow}>
              <strong>TIN Number:</strong>
              <span>{supplier.tinNumber}</span>
            </div>
          )}
          {supplier.notes && (
            <div className={styles.detailRow}>
              <strong>Notes:</strong>
              <span>{supplier.notes}</span>
            </div>
          )}
          <div className={styles.detailRow}>
            <strong>Status:</strong>
            <span>
              {supplier.isVoided ? (
                <span className={styles.voidedBadge}>Voided</span>
              ) : (
                <span className={styles.activeBadge}>Active</span>
              )}
            </span>
          </div>
          {supplier.isVoided && supplier.voidedAt && (
            <div className={styles.detailRow}>
              <strong>Voided At:</strong>
              <span>{formatDate(supplier.voidedAt)}</span>
            </div>
          )}
          {supplier.isVoided && supplier.voidReason && (
            <div className={styles.detailRow}>
              <strong>Void Reason:</strong>
              <span>{supplier.voidReason}</span>
            </div>
          )}
          <div className={styles.detailRow}>
            <strong>Created:</strong>
            <span>{formatDate(supplier.createdAt)}</span>
          </div>
          <div className={styles.detailRow}>
            <strong>Last Updated:</strong>
            <span>{formatDate(supplier.updatedAt)}</span>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.modalFooter}>
        <Button type="button" variant="outline" onClick={onHide}>
          Close
        </Button>
        {!supplier.isVoided && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onEdit();
                onHide();
              }}
            >
              <Pencil size={16} style={{ marginRight: '0.5rem' }} />
              Edit
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onDelete();
                onHide();
              }}
              className={styles.deleteButton}
            >
              <Trash2 size={16} style={{ marginRight: '0.5rem' }} />
              Void
            </Button>
          </>
        )}
        {supplier.isVoided && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onRestore();
              onHide();
            }}
            className={styles.restoreButton}
          >
            <RotateCcw size={16} style={{ marginRight: '0.5rem' }} />
            Restore
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}

export default ViewSupplierModal;
